import {GridPhoto} from 'platform/components';

import {pipe, propEq, prop, defaultTo, filter, map, sortBy, chain, uniqBy} from 'ramda';

import {
  AuditDataAssetsBody,
  AuditDataAssetsFilesBody,
  AuditDataVideoAssetsBody,
  LoadAuditDataCategories,
} from '@dms/api/core';
import {ServiceAlbumPhotoResponseBody, VehicleAlbumPhotoV2ResponseBody} from '@dms/api/sales';
import {VehiclePhotoResponseBody} from '@dms/api/shared';

import {Video} from '../components/VideoPreview/types/video';

// Storage URLs expose the same asset through a `/resize/` (thumbnail) and a `/get/` (full-size)
// path segment, e.g. https://storage.../resize/<id>?token=... → https://storage.../get/<id>?token=...
// Anchoring on the `/resize/` path segment (rather than a bare `resize` substring) avoids
// corrupting hosts, tokens, or filenames that merely contain the text "resize".
export const getFullSizeStorageUrl = (url: string): string => url.replace('/resize/', '/get/');

export const getGridPhotoFullSizeUrl = (
  photo: Pick<GridPhoto, 'originalUrl' | 'url' | 'resizeUrl'>
): string => getFullSizeStorageUrl(photo.originalUrl || photo.url || photo.resizeUrl || '');

type ServiceCaseAlbumPhoto =
  | ServiceAlbumPhotoResponseBody
  | VehicleAlbumPhotoV2ResponseBody
  | VehiclePhotoResponseBody;

type AlbumVideo = ServiceAlbumPhotoResponseBody | VehicleAlbumPhotoV2ResponseBody;

// Service-album, vehicle-album (V2) and — since T20-128120 — V1 vehicle-photo items carry a
// `mediaType` discriminator. The `in` check guards against items where the field is absent at
// runtime (e.g. sale-vehicle photos merged into the same array); those are treated as images.
export const isAlbumVideo = (photo: ServiceCaseAlbumPhoto): photo is AlbumVideo =>
  'mediaType' in photo && photo.mediaType === 'video';

const isUnknownAlbumMedia = (photo: ServiceCaseAlbumPhoto): photo is AlbumVideo =>
  'mediaType' in photo && photo.mediaType === 'unknown';

const mapAlbumVideo = (photo: AlbumVideo): Video => ({
  id: photo.id,
  videoId: photo.fileId,
  url: photo.url,
  originUrl: photo.url,
  previewUrl: null,
  coverImageUrl: photo.thumbnailUrl,
  variants: photo.variants?.map((variant) => ({type: variant.type, url: variant.url})) ?? null,
});

export const getGridServiceAlbumPhotos = (photos?: ServiceCaseAlbumPhoto[]): GridPhoto[] => {
  const allPhotos = photos ?? [];

  // The same file can arrive from two endpoints: the service/vehicle album (carrying a `mediaType`)
  // and the V1 vehicle-photos endpoint (`VehiclePhotoResponseBody`, which has no `mediaType`).
  const nonPhotoFileIds = new Set(
    allPhotos
      .filter((photo) => isAlbumVideo(photo) || isUnknownAlbumMedia(photo))
      .map((photo) => photo.fileId)
  );

  return sortBy(
    (photo: ServiceCaseAlbumPhoto) => photo.weight,
    uniqBy(
      (photo: ServiceCaseAlbumPhoto) => photo.fileId,
      allPhotos.filter((photo) => !nonPhotoFileIds.has(photo.fileId))
    )
  ).map(
    (photo): GridPhoto => ({
      url: photo.url,
      originalUrl: getFullSizeStorageUrl(photo.url),
      resizeUrl: photo.url,
      makeUrl: (url: string, width: number) => `${url}&width=${width}`,
      id: photo.id,
    })
  );
};

export const getAlbumVideos = (photos?: ServiceCaseAlbumPhoto[]): Video[] =>
  sortBy(
    (photo: AlbumVideo) => photo.weight,
    uniqBy((photo: AlbumVideo) => photo.fileId, (photos ?? []).filter(isAlbumVideo))
  ).map(mapAlbumVideo);

export const getAlbumUnknownMedia = (photos?: ServiceCaseAlbumPhoto[]): Video[] =>
  sortBy(
    (photo: AlbumVideo) => photo.weight,
    uniqBy((photo: AlbumVideo) => photo.fileId, (photos ?? []).filter(isUnknownAlbumMedia))
  ).map(mapAlbumVideo);

export const getGridPhotos = (albumId?: string) =>
  pipe<
    [VehiclePhotoResponseBody[] | undefined],
    VehiclePhotoResponseBody[],
    VehiclePhotoResponseBody[],
    VehiclePhotoResponseBody[],
    GridPhoto[]
  >(
    defaultTo([]),
    filter(propEq(albumId, 'albumId')),
    sortBy(prop('weight')),
    map((photo) => ({
      url: photo.url,
      originalUrl: getFullSizeStorageUrl(photo.url),
      resizeUrl: photo.url,
      makeUrl: (url, width) => `${url}&width=${width}`,
      id: photo.id,
    }))
  );

export const getGridPhotosFromAudit = (auditCategories?: LoadAuditDataCategories[]) =>
  pipe(
    defaultTo<LoadAuditDataCategories[]>([]),
    chain((category: LoadAuditDataCategories) => category.assets),
    chain((audit: AuditDataAssetsBody) => audit.files),
    uniqBy(prop('uploadedAssetId')),
    map<AuditDataAssetsFilesBody, GridPhoto>((file) => ({
      url: file.url,
      originalUrl: getFullSizeStorageUrl(file.url),
      resizeUrl: file.resizeUrl,
      id: file.uploadedAssetId,
      makeUrl: (url, width) => `${url}&width=${width}`,
    }))
  )(auditCategories);

export const getGridVideosFromAudit = (auditCategories?: LoadAuditDataCategories[]) =>
  pipe(
    defaultTo<LoadAuditDataCategories[]>([]),
    chain((category: LoadAuditDataCategories) => category.videoAssets),
    chain((audit: AuditDataVideoAssetsBody) => audit.videos),
    uniqBy(prop('videoId'))
  )(auditCategories);
